import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/api";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  const tax = subtotal * 0.06;
  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 9.95;
  const total = subtotal + tax + shipping;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
          <ShoppingBag className="w-10 h-10 text-slate-400" />
        </div>
        <h1 className="mt-6 text-3xl font-black text-slate-900">Your cart is empty</h1>
        <p className="mt-2 text-slate-500">Start shopping to add products to your cart.</p>
        <Button asChild className="mt-6 bg-[#0b3d75] hover:bg-[#0a4e94]"><Link to="/shop">Browse Products</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl md:text-4xl font-black text-slate-900">Shopping Cart</h1>
      <p className="text-slate-500 mt-1">{items.length} item{items.length === 1 ? "" : "s"} in your cart</p>

      <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 p-4 bg-white border border-slate-200 rounded-lg">
              <Link to={`/product/${it.slug}`} className="w-24 h-24 shrink-0 rounded-md overflow-hidden bg-slate-50 border">
                <img src={it.image} alt={it.name} className="w-full h-full object-contain" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.slug}`} className="font-semibold text-slate-900 hover:text-[#0b3d75] line-clamp-2">{it.name}</Link>
                <div className="text-xs text-slate-500 mt-1">SKU: {it.sku} • per {it.unit || "each"}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center border border-slate-300 rounded-md">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="h-9 w-9 hover:bg-slate-100 flex items-center justify-center">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <div className="w-10 text-center text-sm font-bold">{it.quantity}</div>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="h-9 w-9 hover:bg-slate-100 flex items-center justify-center">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="font-black text-[#0b3d75]">{formatMoney(it.price * it.quantity)}</div>
                </div>
              </div>
              <button onClick={() => removeItem(it.product_id)} className="self-start p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <aside className="h-fit sticky top-24 p-5 bg-white border border-slate-200 rounded-lg">
          <h3 className="font-black text-lg text-slate-900">Order Summary</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-semibold">{formatMoney(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Tax (MI 6%)</span><span className="font-semibold">{formatMoney(tax)}</span></div>
            <div className="flex justify-between">
              <span className="text-slate-600">Shipping</span>
              <span className="font-semibold">{shipping === 0 ? "FREE" : formatMoney(shipping)}</span>
            </div>
            {subtotal < 75 && (
              <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-xs">
                <Tag className="w-3.5 h-3.5" />
                Add {formatMoney(75 - subtotal)} more for FREE shipping
              </div>
            )}
          </div>
          <div className="border-t border-slate-200 my-4" />
          <div className="flex justify-between items-baseline">
            <span className="font-black text-lg">Total</span>
            <span className="text-2xl font-black text-[#0b3d75]">{formatMoney(total)}</span>
          </div>
          <Button onClick={() => navigate("/checkout")} className="w-full mt-5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-12">
            Proceed to Checkout <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button asChild variant="outline" className="w-full mt-2 h-11">
            <Link to="/shop">Continue Shopping</Link>
          </Button>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
